import React from 'react';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {action, observable} from 'mobx';
import {Popover} from '@mintdata-wsp/mint-kit/impl/components/popover/Popover';
import bind from 'bind-decorator';
import moment from '@blueprintjs/datetime/node_modules/moment';
import {Tooltip} from '@mintdata-wsp/mint-kit/impl/components/tooltip/Tooltip';
import {PopupAnchor, SourceAnchor} from '@mintdata-wsp/mint-kit/impl/components/popover/PopupTypes';
import debounce from 'lodash/debounce';

const styles = require('./SyncStatus.less');
const cx = classnames.bind(styles);

interface SyncStatusData {
    renderIcon:(props?:any) => JSX.Element;
    buttonTitle:string;
    popoverTitle:string;
    renderMessage:() => JSX.Element;
}

class AllSyncStatusData {
    TimedOut:SyncStatusData = {
        renderIcon: (props) => <MDIcon name={MDIcons.common.SyncronizationProblem} size={MDIconSize.medium14} className={cx('icon', props && props.className)} />,
        buttonTitle: 'Save issue',
        popoverTitle: 'Issue saving design',
        renderMessage: () => <span>
            <p>Your changes are being saved to the cloud, but this is taking longer than usual.</p>
            <p>Please check your network connection to ensure you have Internet access.</p>
            <p className={cx('timedOut')}>
                Do not close this browser tab. Your edits are still being saved to the cloud.
            </p>
        </span>,
    };
    Saving:SyncStatusData = {
        renderIcon: (props) => <MDIcon name={MDIcons.common.Syncronization} size={MDIconSize.small} className={cx('icon', props && props.className)} />,
        buttonTitle: 'Saving...',
        popoverTitle: 'Save in progress',
        renderMessage: () => <span>
            Please do not close this tab until the save is done.
        </span>,
    };
    Saved:SyncStatusData = {
        renderIcon: () => <MDIcon name={MDIcons.common.Checkmark} size={MDIconSize.medium16} className={cx('icon')} />,
        buttonTitle: '',
        popoverTitle: 'Saved to cloud',
        renderMessage: () => <span>
            All design changes have been saved.
        </span>,
    };
}

interface SyncStatusProps {
    minimal?:boolean;
}

@observer
export class SyncStatus extends React.Component<SyncStatusProps>
{
    @observable
    private _isPopuverOpen:boolean = false;

    private _popoverSource:HTMLElement = null;

    private _allSyncStatusData = new AllSyncStatusData();

    private _lastEditUpdateTimer = null;

    private _isJustSaved = false;

    private _debouncedClearJustSavedState = debounce(this._clearJustSavedState, 3000);

    @action.bound
    private _handleClick = () =>
    {
        this._isPopuverOpen = !this._isPopuverOpen;
    };

    @action.bound
    private _hidePopover()
    {
        this._isPopuverOpen = false;

        if (this._lastEditUpdateTimer)
        {
            clearTimeout(this._lastEditUpdateTimer);
            this._lastEditUpdateTimer = null;
        }
    }

    public componentWillUnmount()
    {
        this._debouncedClearJustSavedState.cancel();
        this._hidePopover();
    }

    public render():JSX.Element
    {
        if( this._isPopuverOpen && !this._lastEditUpdateTimer )
            this._lastEditUpdateTimer = setTimeout(() => this.forceUpdate(), 10000);

        return this._renderButton();
    }

    private _renderButton()
    {
        const {isDesignSaveTimedOut} = rootState.net;

        this._updateJustSavedState();

        const syncStatus = this._getSyncStatusData();

        return (
            <Tooltip
                tooltipClassName={cx('tooltip')}
                showArrow={false}
                closeOnTargetClick={true}
                content={'View syncronization status'}
                popoverProps={{
                    className: cx('tooltipPopover'),
                    sourceAnchor: SourceAnchor.CenterBottom,
                    popupAnchor: PopupAnchor.CenterTop,
                    ignoreEsc: true,
                }}
            >
                <div
                    ref={(el:HTMLElement) => (this._popoverSource = el)}
                    className={cx(
                        'syncStatus',
                        isDesignSaveTimedOut && 'timedOut',
                        this._isPopuverOpen && 'popoverOpen',
                        this.props.minimal  && 'minimal'
                    )}
                    onClick={this._handleClick}
                >

                    {syncStatus.renderIcon({className: this.props.minimal ? 'flicker' : undefined})}

                    {!this.props.minimal && syncStatus.buttonTitle && <span className={cx('message')}>
                        {syncStatus.buttonTitle}
                    </span>}

                    {this._isPopuverOpen && this._renderPopover(syncStatus)}

                </div>
            </Tooltip>
        )
    }

    @bind
    private _updateJustSavedState()
    {
        const {isDesignSaveInProgress, isDesignSaveTimedOut, lastSuccessfulSaveTimestamp} = rootState.net;

        this._isJustSaved = !isDesignSaveInProgress && !isDesignSaveTimedOut && new Date().getTime() - lastSuccessfulSaveTimestamp < 3000;

        this._debouncedClearJustSavedState();
    }

    @bind
    private _clearJustSavedState()
    {
        this._isJustSaved = false;
        this.forceUpdate();
    }

    private _getSyncStatusData():SyncStatusData
    {
        const {isDesignSaveInProgress, isDesignSaveTimedOut} = rootState.net;

        if( isDesignSaveTimedOut )
            return this._allSyncStatusData.TimedOut;
        else if( isDesignSaveInProgress )
            return this._allSyncStatusData.Saving;
        else
            return {
                ...this._allSyncStatusData.Saved,
                buttonTitle: this._isJustSaved ? 'Saved' : ''
            }; 
    }

    @bind
    private _renderPopover(syncStatus:SyncStatusData)
    {
        const {isDesignSaveTimedOut, lastSuccessfulSaveTimestamp} = rootState.net;

        return (
            <Popover
                id="syncStatusPopover"
                onDismissed={this._hidePopover}
                isModal={false}
                popupSource={this._popoverSource}
            >
                <div className={cx('popover')}>

                    <div className={cx('title', isDesignSaveTimedOut && 'timedOut')}>

                        {syncStatus.renderIcon()}

                        <span className={cx('text')}>
                            {syncStatus.popoverTitle}
                        </span>

                    </div>

                    <div className={cx('description')}>
                        {syncStatus.renderMessage()}
                    </div>

                    {
                        lastSuccessfulSaveTimestamp > 0 && <div
                            className={cx('lastSaveTime')}
                            title={moment(lastSuccessfulSaveTimestamp).format('MM-DD-YYYY HH:mm:ss')}
                        >
                            Last edit was {moment(lastSuccessfulSaveTimestamp).fromNow()}
                        </div>
                    }

                </div>
            </Popover>
        )
    }
}